import React, { useMemo } from "react";
import moment from "moment";
import { FormGroup, Label, Row, Col } from "reactstrap";
import { RSelect } from "../../../Component";
import EditMedicalPartnerPrimaryUserField from "./EditMedicalPartnerPrimaryUserField";
import CountryCodeSelect from "../../../common/CountryCodeSelect";
import { mergeCountryFilterOptions, resolveIsoCountrySelectValue } from "../../../../utils/iso3166Alpha2Options";
import { PARTNER_TC_PLATFORM_IN_PROFILE_MODAL_HINT } from "../medicalPartnerAdminFormCopy";
import { PARTNER_TC_PLATFORM_SELECT_OPTIONS } from "../medicalPartnerAdminConstants";
import { buildTimezoneSelectOptions } from "../../../../pages/pre-built/user-manage/medical-partner-details/helpers";
import { LANGUAGE_SELECT_OPTIONS } from "../../../../pages/pre-built/user-manage/medical-partner-details/constants";

/**
 * Organization, primary user (directory), operational phone/address, business, services.
 * Contact name/email are not edited here — they are derived from the selected primary user on save.
 */
const EditMedicalPartnerProfileFormBody = ({
  form,
  setForm,
  partnerId,
  primaryUserFilterPartner,
  selectedPrimaryUser,
  onPrimaryUserChange,
  formDisabled,
  tcSelect = "off",
  onTcSelectChange,
  teleconsultationPlatformUpdatedAt = null,
}) => {
  const timezoneSelectOptions = useMemo(() => buildTimezoneSelectOptions(), []);

  return (
  <Row className="g-3 gy-3">
    <Col md="12">
      <h6 className="overline-title text-primary mb-3">Organization</h6>
    </Col>
    <Col md="12">
      <FormGroup>
        <Label className="form-label">Organization name *</Label>
        <input
          type="text"
          className="form-control"
          value={form.organization_name}
          onChange={(e) => setForm((s) => ({ ...s, organization_name: e.target.value }))}
          autoComplete="organization"
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>

    <Col md="12">
      <h6 className="overline-title text-primary mb-2 mt-1">Programs</h6>
      <p className="text-soft small mb-2">
        Platform TC approval below; per-clinic channels stay on the Teleconsultation tab. Disable medical tourism only for partners who will not sell
        travel-assisted / packaged care.
      </p>
      <div className="custom-control custom-switch">
        <input
          type="checkbox"
          className="custom-control-input"
          id="edit-medical-partner-offers-mt"
          checked={Boolean(form.offersMedicalTourism)}
          onChange={(e) => setForm((s) => ({ ...s, offersMedicalTourism: e.target.checked }))}
          disabled={formDisabled}
        />
        <label className="custom-control-label" htmlFor="edit-medical-partner-offers-mt">
          Medical tourism (packages, travel-assisted care)
        </label>
      </div>
    </Col>

    <Col md="12">
      <h6 className="overline-title text-primary mb-2 mt-1">Teleconsultation (platform)</h6>
      <p className="text-soft small mb-2">{PARTNER_TC_PLATFORM_IN_PROFILE_MODAL_HINT}</p>
      <FormGroup>
        <Label className="form-label" htmlFor={`edit-partner-tc-platform-${partnerId || "new"}`}>
          Platform status
        </Label>
        <select
          id={`edit-partner-tc-platform-${partnerId || "new"}`}
          className="form-control"
          value={tcSelect}
          disabled={formDisabled}
          onChange={(e) => onTcSelectChange(e.target.value)}
        >
          {PARTNER_TC_PLATFORM_SELECT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </FormGroup>
      <FormGroup>
        <Label className="form-label">Internal note (optional)</Label>
        <textarea
          className="form-control"
          rows={2}
          value={form.tcApprovalNote}
          onChange={(e) => setForm((s) => ({ ...s, tcApprovalNote: e.target.value }))}
          disabled={formDisabled}
          placeholder="Shown to admins only"
        />
      </FormGroup>
      {teleconsultationPlatformUpdatedAt ? (
        <p className="text-soft small mb-0">
          Last updated on server {moment(teleconsultationPlatformUpdatedAt).format("DD/MM/YYYY HH:mm")}
        </p>
      ) : null}
    </Col>

    <Col md="12">
      <EditMedicalPartnerPrimaryUserField
        filterPartner={primaryUserFilterPartner}
        value={selectedPrimaryUser}
        onChange={onPrimaryUserChange}
        disabled={formDisabled}
        idPrefix={`edit-medical-partner-primary-${partnerId || "new"}`}
      />
    </Col>

    <Col md="12">
      <h6 className="overline-title text-primary mb-3 mt-2">Operational reachability</h6>
      <p className="text-soft small mb-2">
        Operations phone and postal address. Contact name and email follow the primary directory user (edit on the User tab).
      </p>
    </Col>
    <Col md="7">
      <FormGroup>
        <Label className="form-label" htmlFor={`edit-partner-dial-${partnerId || "new"}`}>
          Country calling code
        </Label>
        <CountryCodeSelect
          id={`edit-partner-dial-${partnerId || "new"}`}
          value={form.contact_person_country_code}
          onChange={(digits) => setForm((s) => ({ ...s, contact_person_country_code: digits }))}
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>
    <Col md="5">
      <FormGroup>
        <Label className="form-label">Phone</Label>
        <input
          type="text"
          className="form-control"
          value={form.contact_person_phone}
          onChange={(e) => setForm((s) => ({ ...s, contact_person_phone: e.target.value }))}
          autoComplete="tel"
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>
    <Col md="12">
      <FormGroup>
        <Label className="form-label">Address</Label>
        <input
          type="text"
          className="form-control"
          value={form.contact_person_address}
          onChange={(e) => setForm((s) => ({ ...s, contact_person_address: e.target.value }))}
          autoComplete="street-address"
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>
    <Col md="4">
      <FormGroup>
        <Label className="form-label">City</Label>
        <input
          type="text"
          className="form-control"
          value={form.contact_person_city}
          onChange={(e) => setForm((s) => ({ ...s, contact_person_city: e.target.value }))}
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>
    <Col md="4">
      <FormGroup>
        <Label className="form-label" htmlFor={`edit-partner-contact-country-${partnerId || "new"}`}>
          Country (ISO)
        </Label>
        <RSelect
          inputId={`edit-partner-contact-country-${partnerId || "new"}`}
          options={mergeCountryFilterOptions([form.contact_person_country])}
          value={resolveIsoCountrySelectValue(form.contact_person_country) || null}
          onChange={(opt) =>
            setForm((s) => ({
              ...s,
              contact_person_country: opt?.value != null ? String(opt.value).trim() : "",
            }))
          }
          placeholder="Select country…"
          isClearable
          isDisabled={formDisabled}
        />
      </FormGroup>
    </Col>

    <Col md="12">
      <h6 className="overline-title text-primary mb-3 mt-2">Business</h6>
    </Col>
    <Col md="6">
      <FormGroup>
        <Label className="form-label">Website</Label>
        <input
          type="text"
          className="form-control"
          value={form.business_website}
          onChange={(e) => setForm((s) => ({ ...s, business_website: e.target.value }))}
          placeholder="https://"
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>
    <Col md="6">
      <FormGroup>
        <Label className="form-label">Registration number</Label>
        <input
          type="text"
          className="form-control"
          value={form.business_registration}
          onChange={(e) => setForm((s) => ({ ...s, business_registration: e.target.value }))}
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>
    <Col md="12">
      <p className="text-soft small mb-0">
        Hotel, accommodation, and translator are set per <strong>clinic</strong> (Clinics tab / partner portal).
      </p>
    </Col>
    <Col md="12">
      <FormGroup>
        <Label className="form-label">Remarks</Label>
        <textarea
          className="form-control"
          rows={3}
          value={form.remarks}
          onChange={(e) => setForm((s) => ({ ...s, remarks: e.target.value }))}
          disabled={formDisabled}
        />
      </FormGroup>
    </Col>

    <Col md="12">
      <h6 className="overline-title text-primary mb-3 mt-2">Regional defaults</h6>
      <p className="text-soft small mb-2">Default timezone and language for partner-facing flows.</p>
    </Col>
    <Col md="6">
      <FormGroup>
        <Label className="form-label" htmlFor={`edit-partner-tz-${partnerId || "new"}`}>
          Default timezone (IANA)
        </Label>
        <RSelect
          inputId={`edit-partner-tz-${partnerId || "new"}`}
          options={timezoneSelectOptions}
          value={
            form.defaultTimezone
              ? timezoneSelectOptions.find((o) => o.value === form.defaultTimezone) || {
                  value: form.defaultTimezone,
                  label: form.defaultTimezone,
                }
              : null
          }
          onChange={(opt) =>
            setForm((s) => ({
              ...s,
              defaultTimezone: opt?.value ? String(opt.value).trim() : "",
            }))
          }
          placeholder="Select timezone…"
          isClearable
          isDisabled={formDisabled}
        />
      </FormGroup>
    </Col>
    <Col md="6">
      <FormGroup>
        <Label className="form-label" htmlFor={`edit-partner-lang-${partnerId || "new"}`}>
          Default language (ISO 639-1)
        </Label>
        <RSelect
          inputId={`edit-partner-lang-${partnerId || "new"}`}
          options={LANGUAGE_SELECT_OPTIONS}
          value={
            LANGUAGE_SELECT_OPTIONS.find(
              (o) => o.value === (form.defaultLanguage || "").trim().toLowerCase()
            ) ||
            (form.defaultLanguage
              ? { value: form.defaultLanguage, label: form.defaultLanguage }
              : null)
          }
          onChange={(opt) =>
            setForm((s) => ({
              ...s,
              defaultLanguage: opt?.value != null ? String(opt.value).trim() : "",
            }))
          }
          placeholder="Select language…"
          isClearable
          isDisabled={formDisabled}
        />
      </FormGroup>
    </Col>
  </Row>
  );
};

export default EditMedicalPartnerProfileFormBody;
